import { atom } from 'jotai';
import {
  tcSelectedDevAreaAtom,
  tcSelectedIsCloneAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import type { SurveyRow } from 'components/survey-description';
import type { SlotComposite } from 'types/api-responses';

/**
 * Whether the Unit Builder is open. That is the ONLY dedicated builder state —
 * the scoped unit itself is derived from the shared map selection
 * (`builderScopedUnitAtom`), so paging the header navigator (which updates
 * `tcSelectedDevAreaAtom`) re-scopes the builder for free and the two can never
 * disagree.
 */
export const builderOpenAtom = atom(false);

/** The scoped unit's identity, as the modal consumes it. */
export interface BuilderScopedUnit {
  /**
   * Development-area id of the scoped unit — null while the builder is on a unit
   * that does not exist yet (a session clone, or a new unit built from scratch),
   * which stays a client-side draft until saved.
   */
  unitId: number | null;
  /** Display name of the scoped unit; null when nothing is selected. */
  unitName: string | null;
}

/**
 * The Unit Builder's scoped unit, derived from the shared selection.
 *
 * A session clone carries a synthetic negative `developmentAreaId` and no server
 * record, so it reads as a draft (`unitId: null`) — the same signal Save, slot
 * generation, and the map layers gate on. `tcSelectedIsCloneAtom` already encodes
 * that check.
 */
export const builderScopedUnitAtom = atom<BuilderScopedUnit>((get) => {
  const sel = get(tcSelectedDevAreaAtom);
  const isClone = get(tcSelectedIsCloneAtom);
  return {
    unitId: isClone ? null : (sel?.developmentAreaId ?? null),
    unitName: sel?.developmentAreaName ?? null,
  };
});

/**
 * The name an unsaved unit will be created under, as typed in the header.
 *
 * Only a draft can be renamed (a clone or a brand-new unit — both report
 * `unitId === null`); a persisted unit's name is read-only in the builder,
 * because Save only ever CREATEs. A brand-new unit has no
 * `tcSelectedDevAreaAtom` entry at all, so there is nowhere else to hold this.
 * `null` = untouched, in which case the scoped unit's own name (or the
 * "New Unit" default) is used. Reset when the builder opens on another unit.
 */
export const builderUnitNameAtom = atom<string | null>(null);

/**
 * Verdict on the draft name, published by the map pane (which owns the
 * `/area_geometry_previews` dry run) and read by the header, so the two share ONE
 * request rather than each firing its own.
 *
 * `errors` are the dry run's reasons the unit cannot be created — a taken name
 * ("Name has already been taken") above all, but also shape refusals. A blank
 * name is deliberately not a server error (the dry run drops it), so the header
 * enforces non-empty itself.
 */
export const builderNameCheckAtom = atom<{
  loading: boolean;
  errors: string[];
}>({ loading: false, errors: [] });

/**
 * True once the user has genuinely RESHAPED the boundary — a vertex drag, a
 * midpoint insert, or a vertex delete.
 *
 * `shapeEdit.dirty` cannot answer this: `useUnitShapeEdit` flips it on *every*
 * `setPieces`, and the survey-preview effect pushes the server-composed geometry
 * through that same setter, so picking a single survey area marks the boundary
 * dirty with no drag at all (and `undo` never clears it). The composition needs
 * to know when NOT to overwrite the operator's shape, so it needs a signal only
 * the map's shape-editor `onChange` sets. Reset per scoped unit and after a
 * successful save.
 *
 * No longer a Save input: the payload is always the boundary as drawn.
 */
export const boundaryUserEditedAtom = atom(false);

/**
 * True once a create has SUCCEEDED for the current draft.
 *
 * Save is normally shut off by the scoped unit becoming real (`unitId !== null`),
 * but that only happens once the refetched unit list reveals the new unit's id
 * (`useAdoptSavedUnit`). This covers the window in between — and covers it for
 * good if adoption never lands — so a second click can never mint a duplicate
 * unit under the same name. It is a guard, not a second source of truth for
 * "existing": every read-only behaviour still derives from `unitId`.
 *
 * Needed because `canSave` short-circuits on `isClone`, and so ignores the row
 * baseline that would otherwise disable Save after a save. Reset per scoped unit.
 */
export const savedDraftAtom = atom(false);

/** Whether "Auto-resolve overlaps" starts on. One definition, so the initial
 *  value and every reset agree. */
export const AUTO_RESOLVE_DEFAULT = true;

/**
 * "Auto-resolve overlaps" — the header toggle shown only for a cloned/unsaved
 * unit. When on, saving the clone force-keeps the new unit through FOUCS (a
 * `force_keep` unit action) so it isn't dropped for overlap. Reset to
 * {@link AUTO_RESOLVE_DEFAULT} each time the builder opens on a new draft, so an
 * operator who unticks it for one unit does not carry that to the next.
 */
export const autoResolveOverlapsAtom = atom(AUTO_RESOLVE_DEFAULT);

/**
 * The builder's arrow-stepped formation band + its zone package id, published by
 * the gun barrel pane so the builder MAP (a separate pane) can scope its own
 * `inventory_unit_slots` fetch to it — decoupled from the main page's
 * `tcHighlightedFormationAtom`. Null when the builder is closed / before the
 * selection resolves.
 */
export const builderSelectedFormationAtom = atom<{
  formation: string | null;
  inventoryFormationPackageId: number | null;
} | null>(null);

/**
 * Bumped when the scoped unit needs re-slotting — currently after a rotate.
 *
 * The request crosses panes: rotate lives in the map toolbar, but generation is
 * owned by the gun barrel pane (one hook, one Pusher subscription). A counter
 * rather than a boolean so consecutive rotates each fire.
 */
export const reslotRequestAtom = atom(0);

/**
 * The slot regeneration a rotate (or its undo) kicked off for the scoped unit across every
 * formation package — what the rotate response handed back, plus the live tally.
 *
 * Set by the rotation hook from the response; advanced by `useUnitRegenerationProgress`, which
 * holds the ONE Pusher subscription (the unit channel) and counts each zone's terminal event
 * against `totalFormations`. Read by the banner in BOTH the builder modal and the main page, so
 * the operator sees the same progress wherever they are. `null` when nothing is regenerating.
 */
export interface UnitRegeneration {
  unitId: number;
  unitName: string | null;
  message: string;
  /** The unit channel every zone run reports on. */
  channel: string;
  /** DISTINCT formations being re-laid — the grain the banner and toasts count in (product ask:
   *  a formation can fan out several zone runs, and counting those overstated the work). */
  totalFormations: number;
  formations: string[];
  /** Zone runs each formation fans out, from the rotate response — a formation is done only
   *  when all of its runs report. */
  formationZoneCounts: Record<string, number>;
  /** Zone runs expected in total — the final refresh (and `done`) waits for all of them. */
  totalZones: number;
  /** Zone runs that reached a terminal status so far, per formation. */
  zonesReported: Record<string, number>;
  /** Zone runs that reached a terminal status so far, in total. */
  totalZonesReported: number;
  /** FORMATIONS whose every zone run has reported. */
  completed: number;
  /** Formations carrying at least one failed zone run. */
  failedFormations: string[];
  /** `failedFormations.length` — kept materialized for the banner/toast. */
  failed: number;
  /** Zone runs (inventories) that failed — the terminal copy's failure count. */
  failedZones: number;
  /** Every zone run has reported — the banner turns terminal, then clears. */
  done: boolean;
}

/**
 * "3 inventories for 2 formations" — the product wording (Misha) every regeneration surface
 * uses to describe the work: the inventory runs being re-laid (one per formation x
 * package/spacing) and the distinct formations they lay. One helper so the banner, the
 * toasts and the rotate confirmation cannot phrase it differently.
 *
 * @param {number} totalZones - Inventory runs being re-laid.
 * @param {number} totalFormations - Distinct formations they lay.
 * @return {string} The scope phrase, both nouns pluralized independently.
 */
export const describeRegenerationScope = (
  totalZones: number,
  totalFormations: number
): string =>
  `${totalZones} inventor${totalZones === 1 ? 'y' : 'ies'} for ` +
  `${totalFormations} formation${totalFormations === 1 ? '' : 's'}`;

export const unitRegenerationAtom = atom<UnitRegeneration | null>(null);

/**
 * True while a rotate's regeneration is still running (started, not every formation reported).
 * What the map's rotate button and the Generate LTD Inventory button key their loading state on:
 * neither may fire another run while the unit is mid re-slot, and the surfaces are only refreshed
 * once when it finishes.
 */
export const unitRegeneratingAtom = atom((get) => {
  const regeneration = get(unitRegenerationAtom);
  return Boolean(regeneration && !regeneration.done);
});

/**
 * Slots scoped for editing, keyed `slot:{slotId}` / `uwi:{uwi}` — the chart's own
 * key format.
 *
 * Shared because scoping is what gates moving a slot, and it can be set from
 * either the chart or the slots table: the mock says "click a slot inside the
 * unit (or tick its row in the table) to scope it — only then can it be moved or
 * trimmed".
 */
export const scopedSlotKeysAtom = atom<ReadonlySet<string>>(new Set<string>());

/**
 * Per-LTD lateral coordinate overlay, keyed by the numeric slot id (the map
 * feature's `id`, i.e. `inventory_unit_slots.id`).
 *
 * The single source of truth for a trimmed lateral. Length is always DERIVED
 * from these two endpoint coords, never stored — so one write re-renders both
 * the map grips and the rail's "Lateral length" field. Front-end only: trims
 * live here until a backend persist path exists, and are discarded when the
 * builder's unit changes. `[lng, lat]` GeoJSON positions, as the map uses.
 */
export const ltdLateralOverridesAtom = atom<
  ReadonlyMap<number, GeoJSON.Position[]>
>(new Map<number, GeoJSON.Position[]>());

/**
 * Numeric slot ids of every selected editable LTD — one for a single selection,
 * several when a group is scoped. Their laterals are trimmed together.
 *
 * Published by the gun barrel pane (which owns the string↔numeric bridge) so the
 * map pane — a separate subtree — knows which laterals to grip without reaching
 * for that bridge itself.
 */
export const selectedLtdSlotIdsAtom = atom<ReadonlySet<number>>(
  new Set<number>()
);

/**
 * The property targeted by a right-click value/unvalue in the Unit Builder gun
 * barrel, or null when the Notes section is closed.
 *
 * Set by the gun barrel (which has the slot data + string↔numeric bridge) and
 * read by the edit rail's Notes section. `valued`/`notes` are the optimistically
 * toggled state; `original*` let Cancel revert. Front-end only — the toggle lives
 * in the shared `slotOverridesAtom`; nothing persists to the backend yet.
 */
export interface ValuationTarget {
  /** Numeric `inventory_unit_slots.id` — the chart/override key. */
  slotId: number;
  /** Display label (string slot id `{unit}_{fmt}_{n}` or UWI). */
  label: string;
  /** Optimistically toggled valued state (false = unvalued). */
  valued: boolean;
  /** Current notes on the property. */
  notes: string | null;
  /** Server value, for Cancel to revert to. */
  originalValued: boolean;
  originalNotes: string | null;
}

export const valuationTargetAtom = atom<ValuationTarget | null>(null);

/**
 * The builder's live Cross-line / In-line setbacks (ft), shared from the gun
 * barrel pane (which owns the spacing controls) to the sibling map pane, which
 * draws the keep-out ring from them. Null values = no band. Preview only — the
 * drawn laterals are re-cut only on Generate.
 */
export const builderSetbackAtom = atom<{
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
}>({ crossLineSetbackFt: null, inLineSetbackFt: null });

/**
 * The unit's survey rows.
 *
 * Shared because both panes need them: the survey panel edits them, and the map
 * derives its quarter grid — the dots it draws and the points vertices snap to —
 * from the tracts they name.
 */
export const surveyRowsAtom = atom<SurveyRow[]>([]);

/**
 * The survey rows as last loaded from (or saved to) the unit — the baseline the
 * Save button diffs `surveyRowsAtom` against to know the user actually *changed*
 * the surveys, versus just opening a unit whose saved rows seed as "content".
 * Seeded on unit load (empty for a new/cloned draft) and reset after a save.
 */
export const surveyRowsBaselineAtom = atom<SurveyRow[]>([]);

/**
 * The open Merge Geometries panel, or null when closed. Shared because the rail
 * edits it and the map draws its end dots and tangent preview.
 *
 * `legSlotIds` are the ids to send: the server swaps inherited slots for this
 * workspace's own copies on the first preview, so they track the latest response.
 * `landing` / `bottomHole` are only what the user picked (no prefill); `preview`
 * is the latest server answer.
 */
export interface MergeDraft {
  legSlotIds: number[];
  landing: { slotId: number; end: 'start' | 'end' } | null;
  bottomHole: { slotId: number; end: 'start' | 'end' } | null;
  preview: SlotComposite | null;
  error: string | null;
  /** A preview request is in flight. */
  loading?: boolean;
  /** A preview copied inherited slots into this workspace (new ids). */
  materialized?: boolean;
}

export const mergeDraftAtom = atom<MergeDraft | null>(null);
